import {groupPartByType, useAuiState} from '@assistant-ui/react';
import React from 'react';

import {maybePluralize} from '../../util/uihelpers';
import {extractReasoningPreview, extractToolArgsPreview} from './previews';
import {WBAgentStepDisclosure} from './WBAgentStepDisclosure';

// MessagePrimitive.GroupedParts distinguishes group nodes from real part
// types by looking for `group-`.
export const STEP_GROUP_KEY = 'group-steps';

export const WBAgentStepGroup: React.FC<
  React.PropsWithChildren<{
    indices: readonly number[];
  }>
> = ({indices, children}) => {
  const parts = useAuiState(s => s.message.parts);
  const isThreadRunning = useAuiState(
    s => s.thread.isRunning && s.message.isLast
  );
  const isActiveRun =
    indices.length > 0 && indices[indices.length - 1] === parts.length - 1;
  const isRunning = isThreadRunning && isActiveRun;

  const stepCount = indices.length;

  let label: string;
  let preview = '';
  if (isRunning) {
    const last = parts[indices[indices.length - 1]];
    if (last?.type === 'reasoning') {
      label = 'Thinking';
      preview = extractReasoningPreview(last.text ?? '');
    } else if (last?.type === 'tool-call') {
      label = last.toolName ?? 'Tool';
      preview = extractToolArgsPreview(last.argsText);
    } else {
      label = 'Thinking';
    }
  } else {
    label = `Worked for ${maybePluralize(stepCount, 'step')}`;
  }
  return (
    <WBAgentStepDisclosure
      icon={isRunning ? 'loading' : 'checkmark2'}
      iconColor={isRunning ? 'moon' : 'green'}
      iconSpin={isRunning}
      isRunning={isRunning}
      stepKind="step_group"
      label={label}
      preview={preview}
      panelClassName="mt-4 flex flex-col rounded-lg border border-moon-250 px-12 py-8 dark:border-moon-700">
      {children}
    </WBAgentStepDisclosure>
  );
};

/**
 * groupAgentSteps is a grouping function for `MessagePrimitive.GroupedParts`.
 * It folds adjacent reasoning and tool-call steps into one group, leaving
 * standalone tool calls (HITL tools, and any tool opting into
 * `display: 'standalone'`) ungrouped.
 */
export const groupAgentSteps = groupPartByType({
  reasoning: [STEP_GROUP_KEY],
  'tool-call': [STEP_GROUP_KEY],
  'standalone-tool-call': [],
});
